import { Injectable } from '@angular/core';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { IFliTotalRow, MovementScheduleHelper } from '@modeso/aumico-lib-notes-fe';
import {
    AccountType,
    IAccount,
    IDisplayOptions,
    IFinancialLineItemComponent,
    IFli,
    IManualAccount,
    IMovementScheduleAccount,
    IMovementScheduleComponent,
    IMovementScheduleFli,
    INotesComponent,
    INotesSection,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ComponentType } from '@modeso/types__aumico-lib-notes-be';
import { cloneDeep } from 'lodash-es';
import { ICurrentAndPreviousFlis } from '../../../interfaces/ICurrentAndPreviousMappedFlis.interface';

@Injectable({ providedIn: 'root' })
export class NotesAdjustmentsAndReclassesHelper {
    constructor(private movementScheduleHelper: MovementScheduleHelper) {}

    public applyAdjustMentsAndReclasses(
        notesAccordions: INotesSection[],
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
    ): INotesSection[] {
        if (notesAccordions == null || flis == null) {
            return [];
        }

        const updatedNotesAccordion = cloneDeep<INotesSection[]>(notesAccordions);
        for (const accordion of updatedNotesAccordion) {
            this.applyAdjustmentsAndReclassesOnComponents(accordion.data, options, flis);
        }

        return updatedNotesAccordion;
    }

    private applyAdjustmentsAndReclassesOnComponents(
        accordionData: INotesComponent[],
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
    ): void {
        for (const dataOfComponents of accordionData) {
            if (dataOfComponents.componentName === ComponentType.MOVEMENT_SCHEDULE) {
                this.applyAdjustmentsAndReclassesOfMovementSchedule(
                    dataOfComponents.data as unknown as IMovementScheduleComponent,
                    options,
                    flis,
                );
            } else if (dataOfComponents.componentName === ComponentType.FINANCIAL_LINE_ITEM) {
                this.applyAdjustmentsAndReclassesOfFli(
                    dataOfComponents.data as unknown as IFinancialLineItemComponent,
                    flis.currentFliIdToAccounts,
                    options,
                );
            }
        }
    }

    private applyAdjustmentsAndReclassesOfMovementSchedule(
        movementsScheduleComponentData: IMovementScheduleComponent,
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
    ): void {
        movementsScheduleComponentData.flis = this.applyAdjustmentsAndReclassesOnMovementScheduleFlis(
            movementsScheduleComponentData.flis,
            flis.currentFliIdToAccounts,
            options,
        );

        if (
            movementsScheduleComponentData.previousMovementScheduleComponent != null &&
            flis.previousFliIdToAccounts.size !== 0
        ) {
            movementsScheduleComponentData.previousMovementScheduleComponent.flis =
                this.applyAdjustmentsAndReclassesOnMovementScheduleFlis(
                    movementsScheduleComponentData.previousMovementScheduleComponent.flis,
                    flis.previousFliIdToAccounts,
                    options,
                );
        }
    }

    private applyAdjustmentsAndReclassesOnMovementScheduleFlis(
        movementScheduleFlis: IMovementScheduleFli[],
        fliIdToAccounts: Map<string, IAccount[]>,
        options: IDisplayOptions,
    ): IMovementScheduleFli[] {
        return movementScheduleFlis
            .filter((fli) => fli.id != '')
            .map((fli) => {
                const accountNumberToAccount = new Map(
                    fliIdToAccounts
                        .get(fli.id)
                        ?.filter(
                            (account) =>
                                account.type === AccountType.BALANCE ||
                                account.type === AccountType.INCOME ||
                                account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
                        )
                        .map((account) => [(account as IAccount).accountNumber, account]),
                );
                let reclassesAccountNumberToAccount: Map<string, IAccount>;
                // relasses must be done first to have the reclasses accounts then adjustment

                if (options.showReClassInSums) {
                    reclassesAccountNumberToAccount = new Map(
                        fliIdToAccounts
                            .get(fli.id)
                            ?.filter((account) => account.type === AccountType.RECLASSES)
                            .map((account) => [(account as IAccount).accountNumber, account]),
                    );

                    fli.accounts = this.applyReclassesValueOnMovementScheduleAccounts(
                        fli.accounts,
                        accountNumberToAccount,
                        reclassesAccountNumberToAccount,
                    );
                } else {
                    reclassesAccountNumberToAccount = new Map<string, IAccount>();
                }
                if (options.showAdjustmentInSums) {
                    // add adjustment value to the accounts
                    fli.accounts = this.applyAdjustmentsValueOnMovementsScheduleAccounts(
                        fli.accounts,
                        accountNumberToAccount,
                        reclassesAccountNumberToAccount,
                    );
                }

                // calculate the totals
                fli.totalRow = this.movementScheduleHelper.calculateTotalOfMovementScheduleFli(fli);
                return fli;
            });
    }

    private applyAdjustmentsValueOnMovementsScheduleAccounts(
        movementScheduleAccounts: IMovementScheduleAccount[],
        accountNumberToAccount: Map<string, IAccount>,
        reclassesAccountNumberToAccount: Map<string, IAccount>,
    ): IMovementScheduleAccount[] {
        return movementScheduleAccounts.map((movementScheduleAccount) => {
            const accountNumber = movementScheduleAccount.id;
            let account: IAccount | undefined;

            if (accountNumberToAccount.has(accountNumber)) {
                account = accountNumberToAccount.get(accountNumber);
            } else if (reclassesAccountNumberToAccount.has(accountNumber)) {
                account = reclassesAccountNumberToAccount.get(accountNumber);
            }

            let closeValue = NumberUtils.roundNumber(movementScheduleAccount.closeValue);
            if (account?.closingAdjustment != null && NumberUtils.isFinite(account.closingAdjustment)) {
                closeValue = NumberUtils.sumNumbers([closeValue, account.closingAdjustment]);
            }

            let openValue = NumberUtils.roundNumber(movementScheduleAccount.openValue);
            if (account?.openingAdjustment != null && NumberUtils.isFinite(account.openingAdjustment)) {
                openValue = NumberUtils.sumNumbers([openValue, account.openingAdjustment]);
            }

            return {
                ...movementScheduleAccount,
                closeValue,
                openValue,
            };
        });
    }

    private applyReclassesValueOnMovementScheduleAccounts(
        movementScheduleAccounts: IMovementScheduleAccount[],
        accountNumberToAccount: Map<string, IAccount>,
        reclassesAccountNumberToAccount: Map<string, IAccount>,
    ): IMovementScheduleAccount[] {
        const movementScheduleReclassesAccountWithReclassesValue: IMovementScheduleAccount[] = [];
        // REV-20899: re-derive `closeValue` / `openValue` from the LIVE leaf
        // account. See `applyReclassesValueOnFliAccounts` for the long
        // explanation — same root cause. Legacy ARs whose movement-schedule
        // notes were saved before FlisMapper baked reclass into the
        // persisted values would otherwise display bare balance with no
        // reclass. Using the live account guarantees the right total for
        // both legacy and v1 ARs. Phantom reclass entries are appended
        // separately below.

        const movementScheduleAccountsWithReclassesValue = movementScheduleAccounts.map((movementScheduleAccount) => {
            const key = movementScheduleAccount.id != null ? movementScheduleAccount.id.toString() : '';
            const account = accountNumberToAccount.get(key);
            if (account == null) {
                return {
                    ...movementScheduleAccount,
                    closeValue: NumberUtils.isFinite(movementScheduleAccount.closeValue)
                        ? NumberUtils.roundNumber(movementScheduleAccount.closeValue)
                        : 0,
                    openValue: NumberUtils.isFinite(movementScheduleAccount.openValue)
                        ? NumberUtils.roundNumber(movementScheduleAccount.openValue)
                        : 0,
                };
            }
            const balance = NumberUtils.isFinite(account.calculatedClosingBalance) ? account.calculatedClosingBalance : 0;
            const reclass = NumberUtils.isFinite(account.closingReClass) ? account.closingReClass : 0;
            const prevBalance = NumberUtils.isFinite(account.calculatedOpeningBalance) ? account.calculatedOpeningBalance : 0;
            const prevReclass = NumberUtils.isFinite(account.openingReClass) ? account.openingReClass : 0;
            return {
                ...movementScheduleAccount,
                closeValue: NumberUtils.roundNumber(balance + reclass),
                openValue: NumberUtils.roundNumber(prevBalance + prevReclass),
            };
        });

        // Since REV-20899 the live FLI lists (FlisMapper) contain the phantom
        // reclass accounts as normal rows, so movement schedules whose FLIs
        // were (re)selected after that change already carry them among the
        // saved rows. Appending every `reclassesAccounts` entry unconditionally
        // would render those accounts twice. Only append entries that are not
        // already present among the saved rows; schedules saved before
        // REV-20899 don't contain the phantoms and still get the append
        // exactly once.
        const savedRowKeys = new Set(
            movementScheduleAccounts.map((row) => this.reclassAccountDedupeKey(row.id, row.text)),
        );
        for (const account of reclassesAccountNumberToAccount.values()) {
            if (savedRowKeys.has(this.reclassAccountDedupeKey(account.accountNumber, account.name))) {
                continue;
            }
            let closeValue = 0;
            if (account.closingReClass != null && NumberUtils.isFinite(account.closingReClass)) {
                closeValue = NumberUtils.roundNumber(account.closingReClass);
            }

            let openValue = 0;
            if (account.openingReClass != null && NumberUtils.isFinite(account.openingReClass)) {
                openValue = NumberUtils.roundNumber(account.openingReClass);
            }
            movementScheduleReclassesAccountWithReclassesValue.push({
                id: account.accountNumber,
                text: account.name,
                closeValue,
                column1: null,
                column2: null,
                column3: null,
                column4: null,
                openValue,
            });
        }

        // normal accounts must be displayed first then reclasses accounts
        return [...movementScheduleAccountsWithReclassesValue, ...movementScheduleReclassesAccountWithReclassesValue];
    }

    private applyAdjustmentsAndReclassesOfFli(
        fliComponentData: IFinancialLineItemComponent,
        fliIdToAccounts: Map<string, IAccount[]>,
        options: IDisplayOptions,
    ): void {
        if (fliComponentData.flis == null) {
            return;
        }
        fliComponentData.flis = fliComponentData.flis.map((fli) => {
            const accountNumberToAccount = new Map(
                fliIdToAccounts
                    .get(fli.id)
                    ?.filter(
                        (account) =>
                            account.type === AccountType.BALANCE ||
                            account.type === AccountType.INCOME ||
                            account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
                    )
                    .map((account) => [(account as IAccount).accountNumber, account]),
            );
            let reclassesFliAccounts: Map<string, IAccount>;
            // relasses must be done first to have the reclasses accounts then adjustment

            if (options.showReClassInSums) {
                reclassesFliAccounts = new Map(
                    fliIdToAccounts
                        .get(fli.id)
                        ?.filter((account) => account.type === AccountType.RECLASSES)
                        .map((account) => [(account as IAccount).accountNumber, account]),
                );
                fli.accounts = this.applyReclassesValueOnFliAccounts(
                    fli.accounts,
                    accountNumberToAccount,
                    reclassesFliAccounts,
                );
            } else {
                reclassesFliAccounts = new Map<string, IAccount>();
            }
            if (options.showAdjustmentInSums) {
                // add adjustment value to the accounts
                fli.accounts = this.applyAdjustmentsValuesOnFliAccounts(
                    fli.accounts,
                    accountNumberToAccount,
                    reclassesFliAccounts,
                );
            }

            // calculate the totals
            fli.totalRow = this.calculateTotalOfFli(fli);
            return fli;
        });
    }

    private applyAdjustmentsValuesOnFliAccounts(
        fliAccounts: IManualAccount[],
        accountNumberToAccount: Map<string, IAccount>,
        reclassesAccountNumberToAccount: Map<string, IAccount>,
    ): IManualAccount[] {
        // TODO: Remove .filter((account) => account.name !== ''): after refactor fli component, in case fli has no account to emit empty array
        return fliAccounts
            .filter((account) => account.accountName !== '')
            .map((fliComponentAccount) => {
                // TODO: remove the number cast after the fli component is refactored to emit numbers instead of string
                const accountNumber = fliComponentAccount.id;
                let account: IAccount | undefined;

                if (accountNumberToAccount.has(accountNumber.toString())) {
                    account = accountNumberToAccount.get(accountNumber.toString());
                } else if (reclassesAccountNumberToAccount.has(accountNumber.toString())) {
                    account = reclassesAccountNumberToAccount.get(accountNumber.toString());
                }

                let currentValue = NumberUtils.isFinite(fliComponentAccount.currentValue)
                    ? NumberUtils.roundNumber(fliComponentAccount.currentValue)
                    : 0;

                if (account?.closingAdjustment != null && NumberUtils.isFinite(account.closingAdjustment)) {
                    currentValue = NumberUtils.sumNumbers([currentValue, account.closingAdjustment]);
                }

                let previousValue = NumberUtils.isFinite(fliComponentAccount.previousValue)
                    ? NumberUtils.roundNumber(fliComponentAccount.previousValue)
                    : 0;

                if (account?.openingAdjustment != null && NumberUtils.isFinite(account.openingAdjustment)) {
                    previousValue = NumberUtils.sumNumbers([previousValue, account.openingAdjustment]);
                }
                return {
                    ...fliComponentAccount,
                    currentValue,
                    previousValue,
                };
            });
    }

    private applyReclassesValueOnFliAccounts(
        fliAccounts: IManualAccount[],
        mappingAccounts: Map<string, IAccount>,
        reclassesAccounts: Map<string, IAccount>,
    ): IManualAccount[] {
        const fliReclassesAccountsWithReclassesValue: IManualAccount[] = [];
        // TODO: Remove .filter((account) => account.name !== ''): after refactor fli component, in case fli has no account to emit empty array

        // REV-20899: re-derive `currentValue` / `previousValue` from the LIVE
        // leaf account instead of trusting the persisted `fliAccount.currentValue`.
        //
        // History: an earlier fix removed the legacy "add `account.closingReClass`"
        // step here on the assumption that `FlisMapper.getAccountsOfFli` always
        // bakes the reclass into `fliAccount.currentValue` at FLI-pick time and
        // persists it that way on `IFinancialLineItemComponent.flis`. That's
        // true for ARs whose notes were saved AFTER the FlisMapper change —
        // but legacy ARs (notes saved before the change) carry bare
        // `calculatedClosingBalance` with no reclass baked in. Both versions
        // ended up wrong for one side or the other.
        //
        // Re-deriving from the live leaf account (which always carries the
        // correct, version-appropriate `closingReClass` / `openingReClass`
        // either from the legacy reclasses tab or from
        // `applyReclassificationsToFinStatements`) produces the right total
        // regardless of when the notes were saved. The phantom
        // `reclassesAccounts` are appended separately below.
        const liveValueFor = (
            id: number | string,
            saved: { currentValue: number; previousValue: number },
        ): { currentValue: number; previousValue: number } => {
            const key = id != null ? id.toString() : '';
            const account = mappingAccounts.get(key);
            if (account == null) {
                // Account no longer mapped to this FLI (renamed, removed,
                // moved). Fall back to whatever was persisted so the row
                // still renders with its last-known total.
                return {
                    currentValue: NumberUtils.isFinite(saved.currentValue)
                        ? NumberUtils.roundNumber(saved.currentValue)
                        : 0,
                    previousValue: NumberUtils.isFinite(saved.previousValue)
                        ? NumberUtils.roundNumber(saved.previousValue)
                        : 0,
                };
            }
            const balance = NumberUtils.isFinite(account.calculatedClosingBalance) ? account.calculatedClosingBalance : 0;
            const reclass = NumberUtils.isFinite(account.closingReClass) ? account.closingReClass : 0;
            const prevBalance = NumberUtils.isFinite(account.calculatedOpeningBalance) ? account.calculatedOpeningBalance : 0;
            const prevReclass = NumberUtils.isFinite(account.openingReClass) ? account.openingReClass : 0;
            return {
                currentValue: NumberUtils.roundNumber(balance + reclass),
                previousValue: NumberUtils.roundNumber(prevBalance + prevReclass),
            };
        };

        const fliAccountsWithReclassesValue = fliAccounts
            .filter((account) => account.accountName !== '')
            .map((fliAccount) => {
                const live = liveValueFor(fliAccount.id, {
                    currentValue: fliAccount.currentValue,
                    previousValue: fliAccount.previousValue,
                });
                return {
                    ...fliAccount,
                    currentValue: live.currentValue,
                    previousValue: live.previousValue,
                };
            });

        // Since REV-20899 the FLI component refreshes its saved rows from the
        // live FLI list (FlisMapper), which now contains the phantom reclass
        // accounts as normal rows — so notes rendered/saved after that change
        // already carry the phantoms among the saved rows. Appending every
        // `reclassesAccounts` entry unconditionally would render those
        // accounts twice and inflate the FLI total. Only append entries that
        // are not already present among the saved rows; notes saved before
        // REV-20899 don't contain the phantoms and still get the append
        // exactly once.
        const savedRowKeys = new Set(
            fliAccounts.map((fliAccount) => this.reclassAccountDedupeKey(fliAccount.id, fliAccount.accountName)),
        );
        for (const account of reclassesAccounts.values()) {
            if (savedRowKeys.has(this.reclassAccountDedupeKey(account.accountNumber, account.name))) {
                continue;
            }
            let currentValue = 0;
            if (account.closingReClass != null && NumberUtils.isFinite(account.closingReClass)) {
                currentValue = NumberUtils.roundNumber(account.closingReClass);
            }

            let previousValue = 0;
            if (account.openingReClass != null && NumberUtils.isFinite(account.openingReClass)) {
                previousValue = NumberUtils.roundNumber(account.openingReClass);
            }

            fliReclassesAccountsWithReclassesValue.push({
                id: +account.accountNumber,
                accountName: account.name,
                currentValue,
                previousValue,
            });
        }

        // normal accounts must be displayed first then reclasses accounts
        return [...fliAccountsWithReclassesValue, ...fliReclassesAccountsWithReclassesValue];
    }

    /**
     * Composite identity (normalized account number + trimmed name) used to
     * detect whether a reclass account is already present among the saved
     * rows. The account number is normalized through Number() because
     * FlisMapper stores row ids as numbers (`+accountNumber`) while the
     * mapping-side reclass entries keep the raw string. The name is part of
     * the key so a reclass account that merely shares the number of a
     * different saved row is still appended.
     */
    private reclassAccountDedupeKey(
        accountNumber: number | string | null | undefined,
        name: string | null | undefined,
    ): string {
        const rawNumber = `${accountNumber ?? ''}`.trim();
        const numeric = Number(rawNumber);
        const normalizedNumber = rawNumber !== '' && !isNaN(numeric) ? `${numeric}` : rawNumber;
        return `${normalizedNumber}|${`${name ?? ''}`.trim()}`;
    }

    private calculateTotalOfFli(fli: IFli): IFliTotalRow {
        // TODO: remove the number cast after the fli component is refactored to emit numbers instead of string
        let totalCurrentValue = 0;
        let totalPreviousValue = 0;
        for (const account of fli.accounts) {
            if (account.accountName === '' && account.id.toString() === '') {
                continue;
            }

            totalCurrentValue = NumberUtils.sumNumbers([totalCurrentValue, account.currentValue]);
            totalPreviousValue = NumberUtils.sumNumbers([totalPreviousValue, account.previousValue]);
        }

        return {
            accountName: fli.totalRow.accountName,
            currentValue: totalCurrentValue,
            previousValue: totalPreviousValue,
        };
    }
}
